import { useSelector } from 'react-redux'
import { useQuery } from 'react-query'
import { useAuth0 } from '../../contexts/auth0-context'
import endpoints from '../../API/endpoints'
import callAPI from '../../API/callSecureAPI'
import { FIVE_MINUTES_MS } from './queryDefaults'
import { MOCK_ROADMAPS } from './useGetRoadmapsQuery.mocks'

export type Roadmap = {
    _id: string
    product: string
    indication: string
    therapyArea?: string
    createdAt: string
    isActive: boolean
    isMaiaAvailable: boolean
    allowedUsersCount?: number
}

export const ROADMAPS_QUERY_KEY = 'roadmaps'

const THIRTY_SECONDS_MS = 30 * 1000

type UseGetRoadmapsQueryProps = {
    onGetRoadmaps?: (roadmaps: Roadmap[]) => void
}

export const useGetRoadmapsQuery = ({ onGetRoadmaps }: UseGetRoadmapsQueryProps = {}) => {
    const { getTokenSilently, isAuthenticated, isLoading } = useAuth0()

    // @ts-expect-error store is not typed properly
    const organizationId = useSelector((state) => state.userRecord?.organizationId)

    const getRoadmaps = async () => {
        const token = await getTokenSilently?.({ audience: 'https://atlas.aesara.com' })

        const { url, method } = endpoints.getStrategiesByOrganizationId

        const response = await callAPI(url + organizationId + '/strategies', method, token)

        const roadmaps: Roadmap[] = response.data ?? []

        return roadmaps.length ? roadmaps : MOCK_ROADMAPS
    }

    return useQuery<Roadmap[]>([ROADMAPS_QUERY_KEY, organizationId], getRoadmaps, {
        enabled: !!getTokenSilently && !!isAuthenticated && !isLoading,
        cacheTime: FIVE_MINUTES_MS,
        // Short on purpose. refetchOnWindowFocus is on (react-query's default, and this query does
        // not opt out) but it only fires on STALE data -- with a five-minute window, coming back to
        // the tab did nothing, so a grant made in another session took five minutes to appear. The
        // list is small and one org's worth of roadmaps is a cheap request.
        staleTime: THIRTY_SECONDS_MS,
        retry: 3,
        onSuccess: onGetRoadmaps,
    })
}
